import { Auth, getAuth, onAuthStateChanged } from 'firebase/auth'
import { useEffect, useState } from 'react'

import firebaseApp from '@/firebase/config'

import { FbConfigProps, FirebaseUserExtends } from '~types/index'

interface UseFirebaseAuthProps {
  fbConfig: FbConfigProps
  federatedClient: boolean
}

interface UseFirebaseAuthReturn {
  user: FirebaseUserExtends | null
  auth: Auth | null
}

/**
 * Custom hook for managing Firebase authentication
 * Handles Firebase auth initialization and user state changes
 */
export const useFirebaseAuth = ({
  fbConfig,
  federatedClient,
}: UseFirebaseAuthProps): UseFirebaseAuthReturn => {
  const [user, setUser] = useState<FirebaseUserExtends | null>(null)
  const [auth, setAuth] = useState<Auth | null>(null)

  // Initialize Firebase auth
  useEffect(() => {
    if (fbConfig) {
      try {
        setAuth(getAuth(firebaseApp(fbConfig)))
      } catch (error) {
        console.error('Firebase auth initialization error:', error)
        setAuth(null)
      }
    }
  }, [fbConfig])

  // Listen to auth state changes
  useEffect(() => {
    if (federatedClient || !auth) {
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser)
    })

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  }, [auth, federatedClient])

  return { user, auth }
}
